// ============================================
// Save System, replaces SaveSystem.cs
// 6 manual slots + 1 auto-save
// Uses localForage (IndexedDB) for save data,
// localStorage for small prefs/settings
// ============================================

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { isUnityFormat, convertUnitySave } from './save-converter.js';

const SLOT_PREFIX = 'PnO_Save_';
const AUTO_SAVE_KEY = 'PnO_AutoSave';
const CURRENT_SAVE_VERSION = 10;
const SLOT_COUNT = 6;

/* global localforage */

// Configure localForage
localforage.config({
    name: 'PinkedAndOwned',
    storeName: 'saves',
    description: 'Game save data',
});

class SaveSystem {
    constructor() {
        this._activeSlot = 0;
        this._autoSaveEnabled = true;
        this._savingPromise = null;  // serialises save() / autoSave(), reentrant calls await the in-flight one

        // In-memory metadata cache for sync access (UI display, Continue button)
        // Populated on init() and kept in sync on save/delete
        this._metaCache = new Map(); // key → { slot, timestamp, day, playerName, gameVersion }

        // iOS Safari LRU mitigation, see _touchSlotKeys / _armPersistGestureRetry
        this._lastSlotTouch = 0;
        this._persistRetryArmed = false;

        // Auto-save on choices ready (mirrors Unity behavior). Suppressed
        // mid-forced-event by reading storyManager.inForcedEvent directly, 
        // the previous design cached the flag via FORCED_EVENT_STARTED /
        // TAG_RETURN listeners and silently broke when a forced event ended
        // without a #return:done/abort tag, sticking the flag true for the
        // rest of the session (foundby:Ewokj v0.2.4.2, auto stuck day 19,
        // player kept playing to day 50).
        events.on(Events.STORY_CHOICES_READY, () => {
            if (this._autoSaveEnabled && !storyManager.inForcedEvent) {
                this.autoSave();
            }
        });
    }

    /**
     * Initialize: load metadata from all slots into cache.
     * Call once at app startup (before rendering menu).
     */
    async init() {
        // Request persistent storage to prevent Safari ITP / browser eviction
        // (best-effort, may be denied without user prompt).
        //
        // Two-stage approach:
        //   1. Boot: only CHECK persisted() status (cheap, no permission prompt).
        //   2. First user gesture: REQUEST persist() from inside the gesture
        //      handler. WebKit's grant heuristic favours gesture context, a
        //      boot-time call without any click outside a Home-Screen-installed
        //      web app silently denies on iOS Safari every time.
        //
        // Firefox quirk: `navigator.storage.persisted()` can hang forever in
        // contexts where no user-visible permission doorhanger is possible
        // (headless/automation, iframes without focus, some extension hosts).
        // We race against a 2s timeout so init() can't deadlock.
        let persistDenied = false;
        if (navigator.storage && navigator.storage.persisted) {
            try {
                const persisted = await Promise.race([
                    navigator.storage.persisted(),
                    new Promise((res) => setTimeout(() => res(null), 2000)),
                ]);
                if (persisted === true) {
                    console.log('[SaveSystem] Persistent storage: already granted');
                } else if (persisted === false) {
                    this._armPersistGestureRetry();
                } else {
                    console.log('[SaveSystem] persisted() timed out, arming gesture retry anyway');
                    this._armPersistGestureRetry();
                }
            } catch(e) { /* not supported */ }
        }

        // Probable-private-mode probe. Chrome/Edge incognito + Brave shields +
        // Safari Private all have estimate().quota in the 50-200MB range vs
        // 5-50GB in normal mode. Combined with persist() denial it's a strong
        // signal that the tab will lose all saves on close. We surface the
        // event once per session, main.js shows a one-time banner unless the
        // user dismissed it (LS flag, but LS is wiped on close in private mode
        // so the banner re-appears every session, which is the correct UX).
        try {
            if (navigator.storage && navigator.storage.estimate) {
                const est = await Promise.race([
                    navigator.storage.estimate(),
                    new Promise((res) => setTimeout(() => res(null), 1500)),
                ]);
                const quota = est?.quota || 0;
                const isMobile = /iPhone|iPad|iPod|Android/.test(navigator.userAgent);
                // Threshold: 150MB. Mobile quotas vary so we skip them here, 
                // the iOS milestone toast (main.js Day 5) covers iOS Safari.
                if (!isMobile && quota > 0 && quota < 150 * 1024 * 1024) {
                    console.warn(`[SaveSystem] Probable private mode: quota=${Math.round(quota/1024/1024)}MB, persistDenied=${persistDenied}`);
                    // Defer event until UI is ready (main.js bindSystemEvents).
                    this._privateModeDetected = { quota, persistDenied };
                }
            }
        } catch(e) { /* estimate unsupported, silent */ }

        const keys = [];
        for (let i = 0; i < SLOT_COUNT; i++) keys.push(this._slotKey(i));
        keys.push(AUTO_SAVE_KEY);

        // Try localForage first, fall back to localStorage for migration
        for (const key of keys) {
            try {
                let data = null;
                // IDB read can THROW in private browsing (Firefox Private,
                // Brave shields). Inner try lets the LS fallback below run
                // even when IDB blows up, outer catch only covered null.
                try {
                    data = await localforage.getItem(key);
                } catch (e) {
                    console.warn(`[SaveSystem] IDB read failed during init for ${key}:`, e.message);
                }

                // Recover from localStorage if localForage is empty or threw
                // (itch.io iframe IndexedDB can vanish on reload; private
                // browsing may block IDB outright).
                if (!data) {
                    const raw = localStorage.getItem(key);
                    if (raw) {
                        data = JSON.parse(raw);
                        try { await localforage.setItem(key, data); }
                        catch (idbErr) { console.warn(`[SaveSystem] IDB recovery write failed for ${key} (LS-only mode):`, idbErr?.message); }
                        // Keep localStorage copy as persistent fallback
                        console.log(`[SaveSystem] Recovered ${key} from localStorage fallback`);
                    }
                }

                // F20: Dual-write watchdog. Both mirrors exist? Compare
                // timestamps. If localStorage copy is newer (e.g. quota
                // exhausted IDB mid-write last session), prefer it and
                // re-sync the stale IDB. Emit telemetry so divergence is
                // trackable.
                if (data) {
                    try {
                        const lsRaw = localStorage.getItem(key);
                        if (lsRaw) {
                            const lsData = JSON.parse(lsRaw);
                            const idbTs = data?.savedAt || data?.timestamp || 0;
                            const lsTs = lsData?.savedAt || lsData?.timestamp || 0;
                            if (lsTs > idbTs + 1000) {
                                // localStorage is measurably newer, IDB fell behind
                                console.warn(`[SaveSystem] Dual-write drift on ${key}: LS=${lsTs} IDB=${idbTs}, preferring LS`);
                                data = lsData;
                                await localforage.setItem(key, data);
                                events.emit(Events.SAVE_HEALTH_ISSUE, {
                                    key, reason: 'dual-write-drift',
                                    lsNewerBy: lsTs - idbTs,
                                });
                            } else if (idbTs > lsTs + 1000) {
                                // IDB newer, re-sync LS so next recovery works
                                try { localStorage.setItem(key, JSON.stringify(data)); }
                                catch (qe) { console.warn(`[SaveSystem] LS re-sync failed for ${key} (quota?):`, qe?.message); }
                            }
                        }
                    } catch (e) {
                        // Watchdog itself must never break the load path.
                        console.warn('[SaveSystem] watchdog non-fatal error:', e);
                    }
                }

                // Unwrap Unity export wrapper (e.g. {PnO_GameSave_Slot1: "..."})
                if (data && typeof data === 'object') {
                    const wrapperKey = Object.keys(data).find(k => k.startsWith('PnO_') && k !== key);
                    if (wrapperKey && Object.keys(data).length === 1) {
                        let inner = data[wrapperKey];
                        if (typeof inner === 'string') inner = JSON.parse(inner);
                        data = inner;
                        console.log(`[SaveSystem] Unwrapped Unity export in ${key}`);
                    }
                }

                // Auto-convert Unity format saves (parallel arrays → objects)
                if (data && isUnityFormat(data)) {
                    console.log(`[SaveSystem] Converting Unity save in ${key} to web format`);
                    data = convertUnitySave(data);
                    await localforage.setItem(key, data);
                }

                if (data) {
                    this._metaCache.set(key, this._extractMeta(key, data));
                }
            } catch (e) {
                console.warn(`[SaveSystem] Failed to load meta for ${key}:`, e);
            }
        }

        // Legacy Unity-save auto-import was removed in v0.2.8 (it relied on a
        // startup registry-query subprocess in the desktop build that a few
        // antivirus heuristics flagged). Players can still import an old Unity
        // save via the file-import dialog.

        // Save-loss detection: if user has played before but no saves found,
        // storage was likely wiped by Safari ITP, private mode, or quota eviction
        try {
            const playedBefore = localStorage.getItem('PnO_HasPlayed');
            const hasAnySaves = this._metaCache.size > 0;
            if (playedBefore && !hasAnySaves) {
                // Defer event emission until UI is ready
                this._saveLossDetected = true;
                this._lastPlayedDate = playedBefore;
                console.warn('[SaveSystem] Save loss detected, user played before but no saves found');
            }
        } catch(e) {}
    }

    /**
     * Returns true if init detected lost saves (user played before, none found now).
     * Emits SAVE_LOSS_WARNING event for UI to display warning.
     */
    checkSaveLoss() {
        if (this._saveLossDetected) {
            events.emit(Events.SAVE_LOSS_WARNING, { lastPlayed: this._lastPlayedDate });
            this._saveLossDetected = false;
        }
        if (this._privateModeDetected) {
            events.emit(Events.SAVE_PRIVATE_MODE, this._privateModeDetected);
            this._privateModeDetected = null;
        }
    }

    /** Mark that the user has actively played (saves should exist after this). */
    markPlayed() {
        try {
            localStorage.setItem('PnO_HasPlayed', new Date().toISOString());
        } catch(e) {}
    }

    // ---- Slot management ----

    get activeSlot() { return this._activeSlot; }

    setActiveSlot(slot) {
        this._activeSlot = Math.max(0, Math.min(slot, SLOT_COUNT - 1));
    }

    // ---- Core save/load ----

    /**
     * Collect all game state into a SaveData object.
     * Systems push their state via a collector pattern to avoid circular imports.
     * @param {object} collectors - { getStory, getVariables, getInventory, getSMS, getCharacterData }
     */
    buildSaveData(collectors) {
        const story = collectors.getStory?.();
        const vars = collectors.getVariables?.();
        const inv = collectors.getInventory?.();
        const sms = collectors.getSMS?.();
        const stats = collectors.getStatistics?.();
        const achievements = collectors.getAchievements?.();
        const character = collectors.getCharacterData?.();
        const perks = collectors.getPerkSelections?.();
        const gallery = collectors.getGalleryUnlocked?.();

        // Critical Ink VARs that must survive a recovery load (inkState=null).
        // game-loader.js step 8a re-applies these on top of defaults so apps
        // the player already unlocked don't reset to "uninstalled" silently.
        const inkVarSnapshot = {};
        if (story?.variablesState) {
            // App-install flags (boolean, snapshot only if true)
            const installKeys = [
                'grindr_installed', 'sissysocial_installed', 'jobfinder_installed',
                'hypno_app_installed', 'onlyfems_installed', 'dareme_installed',
                'debug_console_installed',
            ];
            for (const k of installKeys) {
                try {
                    const v = story.variablesState[k];
                    if (v === true || v === 1) inkVarSnapshot[k] = true;
                } catch(e) { /* ignore */ }
            }
            // Route-defining VARs (any value, snapshot if not at Ink default).
            // Audit 2026-05-24 G1: recovery loads were losing sarah_arc /
            // master_intensity / office_route / keyholder_npc / permanent_lock /
            // ending_reached / post_ending, kompletter Run weg.
            const routeKeys = [
                'sarah_arc', 'master_intensity', 'office_route',
                // Pacing 2.0 (PACING_RETUNE.md): pacing_target_days defines the whole
                // arc length (endgame floor + scaled beats); pacing_migrated guards the
                // old-save backfill; pacing_mode is the derived bucket that ~60 legacy
                // reads still gate on; speed_signal_active drives the phase-lock bypass.
                // A recovery-load (inkState=null) without ALL of these would reset a chosen
                // run to Ink defaults (target 60 / mode 1 / signal false) — an inconsistent
                // combo (e.g. target 90 restored but mode 1 defaulted).
                'pacing_target_days', 'pacing_migrated', 'pacing_mode', 'speed_signal_active',
                // Estrogen Patch drives a temporary speed_signal_active bypass; snapshot
                // its countdown so a recovery-load (inkState null) doesn't strand
                // speed_signal_active=true forever with no overnight tick to expire it.
                'pharm_estrogen_days',
                // Fractional gain accumulators — banked sub-1 Fem/Sub progress. Ink-only
                // (inkState); snapshot so a recovery-load doesn't drop the partial point.
                'fem_accum', 'sub_accum',
                // Sarah-Domme (sarah_arc==5) route-shaping: the KeptByHer 0.3
                // ending gates on these; without them a recovery-load (inkState
                // null) drops a committed Sarah-domme run's ending eligibility.
                'sarah_dom_intensity', 'sarah_dom_rules_set',
                // BNWO arc (0.3): opt-in + progression, route-shaping (recovery-load survival, BNWO_ARC.md §11)
                'pref_bnwo', 'university_discovered', 'bnwo_stage', 'bnwo_devotion',
                // BNWO curiosity-descent rework (0.3.1, BNWO_ARC.md §16): origin +
                // doubt/relapse state must survive recovery loads (a dropped
                // bnwo_doubt_stage would re-fire the doubt beat; a dropped
                // bnwo_seed would relock the Recommended category).
                'bnwo_seed', 'bnwo_lecture_attended', 'bnwo_doubt_stage', 'bnwo_stayaway_day', 'bnwo_envelope_taken', 'bnwo_cooloff_until',
                // Free Use track (0.3.1, FREE_USE.md §3): opt-in + progression. Snapshot ANY
                // non-null (incl. stage 0 / count 0) so a recovery-load doesn't drop a
                // discovered-but-early track back to Ink defaults. free_use_count gates the
                // self-heal achievements; sms_free_use_stage keeps the store choice reachable.
                'pref_free_use', 'free_use_stage', 'free_use_count', 'sms_free_use_stage',
                // Kennel Expansion (0.3.1, KENNEL_EXPANSION.md §2): graduation +
                // pet name + stage-4 flags are route-shaping, a recovery-load
                // without them re-fires the graduation ceremony and loses the
                // Mira-given name. Snapshot ANY non-null (pet_name may be "").
                // pref_pet_play (§10.2 retrofit): Ink default is TRUE (grandfather),
                // so a post-arm opt-OUT (false) MUST survive a recovery load or the
                // player's explicit off-toggle silently reverts to on.
                'mira_collar_owned', 'pet_name', 'kitten_stage_4_done', 'puppy_stage_4_done', 'kennel_overnight_done', 'pref_pet_play',
                // Alex debt ladder (0.3): strike/debt state is persistent pressure
                // (ALEX_DEBT_CONSEQUENCES.md §6). Snapshot ANY non-null incl. 0/false
                // — a recovery-load dropping an armed final notice would silently
                // forgive the collection.
                'alex_debt_strikes', 'alex_overdue', 'alex_debt_sms_offered', 'alex_final_notice', 'alex_collection_count',
                // Cabaret venue (0.3, CABARET_VENUE.md §10): job progression +
                // the exhib_fem exhibition axis are Ink-only — a recovery-load
                // (inkState=null) without these resets the performer career and
                // the whole exhibition track to zero. Snapshot ANY non-null.
                'cabaret_discovered', 'cabaret_stage', 'cabaret_shifts', 'cabaret_reputation',
                'cabaret_manager_rel', 'cabaret_regular_apollo', 'cabaret_regular_vera',
                'exhib_fem', 'exhib_fem_tier',
                // MASC_ROUTE (0.2.8): route-shaping markers, without these a
                // recovery-load (inkState=null) resets a masculine run to the
                // default feminize path. Snapshot ANY non-null incl. 0.
                // master_truce / masc_key_returned: one-shot reclaim beats
                // (Owner standoff, keyholder key-return) — audit 2026-06-07.
                'masc_path', 'orientation_lean', 'master_truce', 'masc_key_returned', 'fem_peak',
                // BOYFRIEND_START (0.3.1): partner route marker — a recovery-load
                // dropping it would silently flip a boyfriend run back to Sarah prose.
                'partner_is_male',
                // MASC_ROUTE §5 dating partners (HETERO_PARTNER.md), route-shaping
                'matched_rachel', 'dating_rachel', 'matched_mara', 'dating_mara', 'matched_cole', 'dating_cole',
                'keyholder_npc', 'permanent_lock', 'permanent_lock_day', 'ending_reached', 'post_ending',
                // Transformation Ladder capstone: one-way flag that lifts the Fem cap to
                // 200 (pink zone). Route-shaping — a recovery-load must not re-lock it.
                'fully_transformed', 'fully_transformed_announced',
                // Ladder progression a recovery-load (inkState null) would otherwise
                // silently zero: the full HRT arc (the capstone gates on hrt_stage /
                // taking_hormones, so losing it can un-earn a near-complete run) + the
                // semi-permanent taper state + hair-grow progress.
                'hrt_stage', 'taking_hormones', 'hrt_last_injection_day', 'hrt_missed_count',
                'hrt_quitting', 'hrt_quit_day',
                'hair_growing', 'hair_grow_stage', 'hair_grow_last_day',
                // Chastity escape-hatch state: cage_lockpicked drives the
                // keyholder-finds-out consequence beat; chastity_walked_away is
                // the Master-route exit ending. Both route-shaping.
                'cage_lockpicked', 'cage_lockpick_consequence_done', 'cage_lockpick_sms_offered', 'cage_lockpick_tier', 'chastity_walked_away', 'cage_keyholder_released', 'cage_resubmit_offered',
                // Hardship perks (0.2.9): run-shaping difficulty modifiers chosen
                // at creation + the forced-onset one-shot/reprieve state. Without
                // these a recovery load (inkState=null) silently drops the player's
                // hardships and could re-fire the forced-cage offer.
                'hardships_enabled', 'perk_reduced_income', 'perk_less_stream_pay', 'perk_reduced_stamina',
                'perk_arousal_spiral', 'perk_ruined_aftermath', 'perk_slow_burn', 'perk_no_grace',
                'perk_cage_gravity', 'perk_owners_property', 'chastity_forced_offered', 'chastity_forced_reprieve_day',
                // Keyholder display + Earned-Relief window + d60 offer one-shot —
                // recovery-loads lost the relief night / re-fired the perm offer.
                'keyholder_display_name', 'cage_relief_active', 'cage_relief_until', 'chastity_relief_count', 'sms_cage_relief_offered', 'Permanent_Lock_Offer_seen',
                'girlfriend_disabled', 'girlfriend_broken_up', 'moved_out',
                // Master's Orders chain, arc-gating, Ink-only (not in
                // OBSERVED_VARIABLES). Without these a recovery-load resets
                // every order to 0 and the journal shows all 'not started'.
                'master_order_chastity', 'master_order_plug', 'master_order_downsize',
                'master_order_laser', 'master_order_makeup', 'master_order_hair',
                'laser_sessions', 'makeup_skill', 'master_order_reminder_count',
                // ENDGAME_OWNER_EXPANSION (0.2.8) §7: post-collar milestones +
                // one-shot-seen flags gate whole arcs; a recovery-load (inkState
                // null) must not reset them or the d90/d120 beats vanish. The
                // *_done_today lockouts + keptlife_cycle are transient → NOT here.
                'chastity_milestone_d90', 'chastity_milestone_d120',
                'postcollar_settled_seen', 'postcollar_120_seen',
                // Deep-review 2026-06-10 P1-2: 0.2.8 VARs that were missing.
                // endgame_triggered is the ACTUAL ending gate (ending_reached/
                // post_ending are display state) — without it a recovery load
                // could re-fire a finished ending.
                'endgame_triggered',
                // Construction Site job track (Patreon) — hire, employer fork,
                // promotions, lifetime shift count + the three NSFW arc stages
                // and their consent markers.
                'construction_hired', 'construction_job', 'construction_stage',
                'construction_shifts', 'polier_stage', 'trupp_stage',
                'architekt_stage', 'polier_sex_opt', 'trupp_sex_opt',
                'architekt_sex_opt',
                // Chastity cadence counters: d60/permanent-offer pacing +
                // Earned-Relief escalation + lockpick re-lock tier.
                'chastity_days_worn', 'chastity_total_days_locked',
                'chastity_relief_count', 'chastity_milestone_d60',
                'cage_lockpick_tier', 'sarah_keyholder_offered',
                // perk_dating_bonus starting match (set by character editor).
                'matched_tyler',
                // Hank (custom dating NPC, Patreon, by Hursk) — arc-shaping; a
                // recovery-load must keep the match + stage + consummation milestone.
                // (hank_enabled kill-switch stripped 2026-07-07 — Patreon gating
                // carries via is_patreon() + phone.js patreon: true.)
                'matched_hank', 'dating_hank', 'hank_consummated',
                // Tyrone (custom dating NPC, Patreon, credit "Anonymous") — arc-shaping;
                // tyrone_enabled stays OUT (kill-switch arms via Globals default flip).
                'matched_tyrone', 'dating_tyrone', 'tyrone_consummated',
                // HypnoMind (0.2.9, Patreon) — arc unlock + escalation stage +
                // session counter are route-shaping (gate out-of-app triggers +
                // deeper sessions). hypno_app_installed is in installKeys above.
                'hypnomind_unlocked', 'hypnomind_depth', 'hypnomind_consented', 'hypnomind_category', 'hypnomind_stage', 'hypnomind_sessions_done', 'hypnomind_first_public_seen',
                // Sissy Body Training (0.2.9, Patreon) — the ARC GATE only. Stage/
                // tier/progress are sandbox progression (recovery-reset acceptable,
                // consistent with Malik's trainer_stage which is also NOT here).
                'tyler_coach_unlocked',
                // Permanent hair transplant (0.2.9, Kirito QoL) — a one-time paid
                // purchase whose whole point is that it never reverts, so a
                // recovery-load (inkState null) must preserve it or the player's
                // $600 permanent hair silently becomes decaying extensions again.
                // auto_hair stays OUT, matching its convenience-toggle siblings
                // auto_makeup / auto_nails (also not snapshotted).
                'hair_permanent',
            ];
            for (const k of routeKeys) {
                try {
                    const v = story.variablesState[k];
                    if (v !== undefined && v !== null) inkVarSnapshot[k] = v;
                } catch(e) { /* ignore */ }
            }
        }

        const data = {
            saveVersion: CURRENT_SAVE_VERSION,
            gameVersion: collectors.gameVersion || '0.2.1',
            timestamp: new Date().toISOString().slice(0, 16).replace('T', ' '),
            day: vars?.baseVar?.Day ?? vars?.baseStats?.Day ?? 0,
            timeSlot: vars?.baseVar?.TimeSlot ?? vars?.baseStats?.TimeSlot ?? 0,
            playerName: vars?.baseVar?.PlayerName ?? vars?.baseStats?.PlayerName ?? 'Jamie',

            // Ink story state (JSON string)
            inkState: story?.state?.toJson?.() ?? null,

            // Variable manager state (exportState returns baseVar/buffedVar/flags)
            baseStats: vars?.baseVar ?? vars?.baseStats ?? {},
            buffedStats: vars?.buffedVar ?? vars?.buffedStats ?? {},
            flags: vars?.flags ?? {},

            // Inventory
            ownedItemTags: inv?.ownedTags ?? [],
            equippedSlots: inv?.equippedSlots ?? [],
            equippedItemTags: inv?.equippedTags ?? [],
            savedOutfit: inv?.savedOutfit ?? null,

            // SMS messages
            smsMessages: sms ?? [],

            // Statistics
            statistics: stats ?? {},

            // Achievements (redundant with localStorage but ensures save portability)
            unlockedAchievements: achievements ?? [],

            // Character creation data
            characterData: character ?? null,

            // Perks
            perkSelections: perks ?? [],

            // Gallery unlocks
            galleryUnlocked: gallery ?? [],

            // Current location (for restoring after load at choice points)
            currentLocation: collectors.getCurrentLocation?.() ?? '',

            // Recovery snapshot of critical Ink VARs (apps, etc).
            // Only used when inkState is null on load (see game-loader.js).
            inkVars: inkVarSnapshot,
        };

        return data;
    }

    /**
     * Save game state to a numbered slot.
     * @param {object} collectors - state collector functions
     * @param {number} [slot] - slot index (default: activeSlot)
     * @returns {Promise<boolean>}
     */
    async save(collectors, slot = this._activeSlot) {
        // F10: chain promises atomically. Two parallel save() calls used to
        // both see _savingPromise=null at the same moment, both assign their
        // own _doSave promise, and both run concurrently on the same slot, 
        // last-write-wins by localforage's internal serialization but with
        // non-deterministic ordering. Chain off the previous promise instead
        // so saves serialise strictly.
        const prev = this._savingPromise || Promise.resolve();
        const my = prev.catch(() => {}).then(() => this._doSave(collectors, slot));
        this._savingPromise = my;
        try {
            return await my;
        } finally {
            if (this._savingPromise === my) this._savingPromise = null;
        }
    }

    async _doSave(collectors, slot) {
        let idbOk = false, lsOk = false, lastErr = null;
        try {
            events.emit(Events.SAVE_STARTED, slot);
            const data = this.buildSaveData(collectors);
            data.slotIndex = slot;
            const key = this._slotKey(slot);

            // Independent dual-write, either backend success = save success.
            // Private browsing often blocks IDB (Firefox Private, Brave
            // strict, iOS Safari Private) but allows LS. The previous
            // sequential `await localforage.setItem` followed by the LS
            // try/catch made the LS fallback unreachable when IDB threw,
            // so saves silently failed for those players.
            try {
                await localforage.setItem(key, data);
                idbOk = true;
            } catch (e) {
                lastErr = e;
                console.warn('[SaveSystem] IDB write failed (private mode?):', e.message);
            }
            try {
                localStorage.setItem(key, JSON.stringify(data));
                lsOk = true;
            } catch (e) {
                lastErr = e;
                if (idbOk) {
                    console.warn('[SaveSystem] localStorage fallback write failed (quota?):', e.message);
                    events.emit(Events.SAVE_FALLBACK_FAILED, { slot, reason: e.message });
                }
            }

            if (!idbOk && !lsOk) {
                console.error('[SaveSystem] Save failed (no storage available):', lastErr);
                events.emit(Events.SAVE_FAILED, { slot, reason: lastErr?.message || 'no storage available' });
                return false;
            }

            this._metaCache.set(key, this._extractMeta(key, data));
            this.markPlayed();
            events.emit(Events.SAVE_COMPLETED, slot);
            events.emit(Events.GAME_SAVED, { slot, isAuto: false });
            console.log(`[SaveSystem] Saved to slot ${slot} (idb=${idbOk}, ls=${lsOk})`);
            return true;
        } catch (e) {
            console.error('[SaveSystem] Save failed:', e);
            events.emit(Events.SAVE_FAILED, { slot, reason: e.message });
            return false;
        }
    }

    /**
     * Auto-save to dedicated key. Called automatically on STORY_CHOICES_READY.
     * Uses the same collectors registered via registerCollectors().
     */
    async autoSave() {
        if (!this._collectors) return;
        // F10: same chained-promise pattern as save() to serialise writes.
        // If a save() is already in flight, this autoSave coalesces, it
        // waits for the prior write but does NOT trigger its own _doAutoSave
        // (the explicit save just covered the same state).
        if (this._savingPromise) {
            try { await this._savingPromise; } catch(_) {}
            return;
        }
        const my = this._doAutoSave();
        this._savingPromise = my;
        try {
            await my;
        } finally {
            if (this._savingPromise === my) this._savingPromise = null;
        }
    }

    async _doAutoSave() {
        try {
            const data = this.buildSaveData(this._collectors);
            data.slotIndex = -1; // auto-save marker

            // §49 amplifier guard (0.2.9.0.4, foundby:VPS player Day-50 iOS 18.7
            // Safari): NEVER auto-save a totally empty inventory over an existing
            // save. A trustworthy game state always owns >= 1 item; new games grant
            // starter items, and equipped items are always in the owned set; there
            // is no Ink/JS path that empties the inventory legitimately. An empty
            // ownedItemTags here means the item DB failed to load this session
            // (mobile fetch blip / content blocker) and restoreFromSave dropped
            // every tag; persisting that would make the loss permanent (regular
            // clothing has no Ink ownership var to heal from). Skip the silent
            // overwrite. The explicit Save button still works if a player somehow
            // wants to save an empty wardrobe.
            if (Array.isArray(data.ownedItemTags) && data.ownedItemTags.length === 0) {
                console.warn('[SaveSystem] Auto-save skipped: empty inventory (item DB likely failed to load), refusing to overwrite save');
                return;
            }

            // Independent dual-write, see _doSave for rationale.
            let idbOk = false, lsOk = false;
            try {
                await localforage.setItem(AUTO_SAVE_KEY, data);
                idbOk = true;
            } catch (e) {
                console.warn('[SaveSystem] auto-save IDB write failed:', e.message);
            }
            try {
                localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(data));
                lsOk = true;
            } catch (e) {
                if (idbOk) {
                    console.warn('[SaveSystem] localStorage auto-save fallback failed (quota?):', e.message);
                    events.emit(Events.SAVE_FALLBACK_FAILED, { slot: -1, reason: e.message, silent: true });
                }
            }

            if (!idbOk && !lsOk) {
                console.warn('[SaveSystem] Auto-save failed (no storage available)');
                events.emit(Events.SAVE_FALLBACK_FAILED, { slot: -1, reason: 'no storage available', silent: true });
                return;
            }

            this._metaCache.set(AUTO_SAVE_KEY, this._extractMeta(AUTO_SAVE_KEY, data));
            this.markPlayed();
            events.emit(Events.GAME_SAVED, { slot: -1, isAuto: true });

            // iOS Safari LRU mitigation, heartbeat-touch slot keys at most
            // once per 60s so they stay at LRU parity with auto-save. Without
            // this, slot saves (written rarely) age fast under iOS storage
            // pressure and get evicted while auto-save (touched every choice)
            // survives. Fire-and-forget; failures are non-fatal.
            const now = Date.now();
            if (now - this._lastSlotTouch > 60_000 && this._anySlotsExist()) {
                this._lastSlotTouch = now;
                this._touchSlotKeys().catch(err => {
                    console.warn('[SaveSystem] heartbeat touch failed:', err?.message || err);
                });
            }
        } catch (e) {
            console.warn('[SaveSystem] Auto-save failed:', e);
            events.emit(Events.SAVE_FAILED, { slot: -1, reason: e.message });
        }
    }

    /**
     * iOS Safari LRU mitigation: re-write existing slot keys to refresh their
     * last-storage-operation timestamp. iOS evicts storage per-origin on an
     * LRU basis (last storage op, not last read), so slot saves that haven't
     * been touched in hours get evicted long before the auto-save key that
     * gets a fresh write on every Continue click.
     *
     * Best-effort: failures swallowed so a stalled IDB doesn't block a
     * successful auto-save.
     */
    async _touchSlotKeys() {
        for (let slot = 0; slot < SLOT_COUNT; slot++) {
            const key = this._slotKey(slot);
            if (!this._metaCache.has(key)) continue;
            try {
                let data = null;
                try { data = await localforage.getItem(key); } catch(e) {}
                if (!data) {
                    try {
                        const raw = localStorage.getItem(key);
                        if (raw) data = JSON.parse(raw);
                    } catch(e) {}
                }
                if (!data) continue;
                try { await localforage.setItem(key, data); } catch(e) {}
                try { localStorage.setItem(key, JSON.stringify(data)); } catch(e) {}
            } catch(e) {}
        }
    }

    _anySlotsExist() {
        for (let slot = 0; slot < SLOT_COUNT; slot++) {
            if (this._metaCache.has(this._slotKey(slot))) return true;
        }
        return false;
    }

    /**
     * Arm a one-shot listener that calls navigator.storage.persist() from
     * inside the first user-gesture handler. WebKit's grant heuristic gives
     * higher odds when the request comes from a click/touch context than
     * from a boot-time call. Idempotent, re-arm is a no-op.
     */
    _armPersistGestureRetry() {
        if (this._persistRetryArmed) return;
        if (!navigator.storage?.persist) return;
        this._persistRetryArmed = true;

        const handler = async () => {
            document.removeEventListener('click', handler, true);
            document.removeEventListener('touchend', handler, true);
            try {
                const granted = await navigator.storage.persist();
                console.log(`[SaveSystem] persist() on gesture: ${granted ? 'GRANTED' : 'denied'}`);
            } catch (e) {
                console.warn('[SaveSystem] persist() gesture retry failed:', e?.message || e);
            }
        };
        document.addEventListener('click', handler, { capture: true });
        document.addEventListener('touchend', handler, { capture: true, passive: true });
    }

    /**
     * Register state collectors for auto-save (avoids passing them each time).
     * @param {object} collectors
     */
    registerCollectors(collectors) {
        this._collectors = collectors;
    }

    /**
     * E-07 v0.2.5: synchronous last-resort save on tab-close.
     * autoSave() is async (await localforage.setItem), so the IDB write
     * may not land if the tab is closing. localStorage.setItem is sync
     * and the only mechanism guaranteed to flush before unload, so this
     * does a sync LS-only write of the latest collector snapshot. IDB
     * stays out-of-sync until next session's load watchdog re-syncs from
     * LS (save.js:146-164).
     *
     * Called from main.js beforeunload listener. Safe to call multiple
     * times; idempotent same-session.
     */
    flushPendingSync() {
        if (!this._collectors) return;
        if (!this._autoSaveEnabled) return;
        try {
            const data = this.buildSaveData(this._collectors);
            data.slotIndex = -1;
            localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(data));
        } catch (e) {
            // beforeunload, no logging useful, browser is terminating
        }
    }

    /**
     * Load game state from a slot.
     * Returns the parsed SaveData, or null if slot is empty.
     * @param {number} [slot] - slot index (default: activeSlot)
     * @returns {Promise<object|null>}
     */
    async load(slot = this._activeSlot) {
        const key = this._slotKey(slot);
        try {
            let data = null;
            // IDB read can throw (private browsing), inner try keeps the
            // LS fallback path reachable.
            try {
                data = await localforage.getItem(key);
            } catch (e) {
                console.warn(`[SaveSystem] IDB read failed for slot ${slot}:`, e.message);
            }

            // Fallback: localStorage
            if (!data) {
                try {
                    const raw = localStorage.getItem(key);
                    if (raw) {
                        data = JSON.parse(raw);
                        console.warn(`[SaveSystem] Slot ${slot} recovered from localStorage fallback`);
                    }
                } catch(e) {}
            }

            if (!data) return null;

            events.emit(Events.LOAD_STARTED, slot);

            // Migrate if needed
            if (data.saveVersion < CURRENT_SAVE_VERSION) {
                try {
                    data = this._migrate(data, slot);
                    await localforage.setItem(key, data);
                } catch(e) {
                    console.error(`[SaveSystem] Migration failed for slot ${slot}:`, e);
                    throw e;
                }
            }

            this._activeSlot = slot;
            events.emit(Events.LOAD_COMPLETED, slot, data);
            console.log(`[SaveSystem] Loaded slot ${slot}`);
            return data;
        } catch (e) {
            console.error(`[SaveSystem] Failed to load slot ${slot}:`, e);
            return null;
        }
    }

    /**
     * Load auto-save data.
     * @returns {Promise<object|null>}
     */
    async loadAutoSave() {
        try {
            let data = null;
            // IDB read can throw (private browsing).
            try {
                data = await localforage.getItem(AUTO_SAVE_KEY);
            } catch (e) {
                console.warn('[SaveSystem] auto-save IDB read failed:', e.message);
            }

            // Fallback: localStorage (survives iframe IndexedDB wipes,
            // and is the only path that works in some private modes)
            if (!data) {
                try {
                    const raw = localStorage.getItem(AUTO_SAVE_KEY);
                    if (raw) {
                        data = JSON.parse(raw);
                        console.warn('[SaveSystem] Auto-save recovered from localStorage fallback');
                    }
                } catch(e) {}
            }
            // If both exist, pick the newer one
            else {
                try {
                    const raw = localStorage.getItem(AUTO_SAVE_KEY);
                    if (raw) {
                        const lsData = JSON.parse(raw);
                        if (lsData.timestamp && lsData.timestamp > (data.timestamp || '')) {
                            data = lsData;
                            console.warn('[SaveSystem] localStorage auto-save is newer, using it');
                        }
                    }
                } catch(e) {}
            }

            if (!data) return null;
            events.emit(Events.LOAD_STARTED, -1);
            events.emit(Events.LOAD_COMPLETED, -1, data);
            return data;
        } catch (e) {
            console.warn('[SaveSystem] Failed to load auto-save:', e);
            return null;
        }
    }

    // ---- Slot info (for UI), sync from cache ----

    /**
     * Check if a save slot has data (sync, from cache).
     */
    slotExists(slot) {
        return this._metaCache.has(this._slotKey(slot));
    }

    /**
     * Check if auto-save exists (sync, from cache).
     */
    autoSaveExists() {
        return this._metaCache.has(AUTO_SAVE_KEY);
    }

    /**
     * Get lightweight metadata for a slot (sync, from cache).
     * @returns {{ slot, timestamp, day, playerName, gameVersion } | null}
     */
    getSlotMeta(slot) {
        return this._metaCache.get(this._slotKey(slot)) || null;
    }

    /**
     * Get metadata for auto-save slot (sync, from cache).
     */
    getAutoSaveMeta() {
        return this._metaCache.get(AUTO_SAVE_KEY) || null;
    }

    /**
     * Refresh the in-memory meta cache for one slot from a save object that
     * was written to storage OUTSIDE the normal save() path (cloud-save
     * materialize). Keeps getSlotMeta()/slot rows in sync with storage.
     */
    refreshSlotMetaFromData(slot, data) {
        try {
            const key = slot === -1 ? AUTO_SAVE_KEY : this._slotKey(slot);
            if (data) this._metaCache.set(key, this._extractMeta(key, data));
        } catch (e) { console.warn('[SaveSystem] refreshSlotMetaFromData failed:', e?.message); }
    }

    /**
     * Get metadata for all slots (including auto-save).
     * @returns {Array<object|null>} index 0-2 = manual slots, index 3 = auto-save
     */
    getAllSlotMeta() {
        const meta = [];
        for (let i = 0; i < SLOT_COUNT; i++) {
            meta.push(this.getSlotMeta(i));
        }
        meta.push(this.getAutoSaveMeta());
        return meta;
    }

    /**
     * Total count of populated save slots (manual + auto). 0-4.
     * Used for telemetry to track save presence over time.
     */
    getSaveCount() {
        return this._metaCache.size;
    }

    // ---- Slot management ----

    async deleteSlot(slot) {
        const key = this._slotKey(slot);
        await localforage.removeItem(key);
        this._metaCache.delete(key);
        // Dual-write parity (mirrors deleteAutoSave): manual slots are also
        // mirrored to localStorage on save, so removing only the IDB copy
        // leaves a stale LS entry that can resurrect the deleted save on the
        // next load. Clear both.
        try { localStorage.removeItem(key); } catch(e) {}
        console.log(`[SaveSystem] Deleted slot ${slot}`);
    }

    async deleteAutoSave() {
        await localforage.removeItem(AUTO_SAVE_KEY);
        this._metaCache.delete(AUTO_SAVE_KEY);
        // Also clear localStorage fallback, stale entry can resurrect old saves
        try { localStorage.removeItem(AUTO_SAVE_KEY); } catch(e) {}
    }

    // ---- Export/Import ----

    /**
     * Export save data as a downloadable JSON file.
     *
     * Three-stage fallback so the player can always escape with a backup,
     * even in private-browsing modes where storage writes silently failed:
     *   1. IDB read (the normal path)
     *   2. localStorage mirror (LS-only saves from this session)
     *   3. Live game state via buildSaveData(), only valid for the active
     *      slot or auto-save (slot=-1); other slots would mis-export.
     */
    async exportSave(slot = this._activeSlot) {
        const key = this._slotKey(slot);
        let data = null;

        try { data = await localforage.getItem(key); } catch(e) {}
        if (!data) {
            try {
                const raw = localStorage.getItem(key);
                if (raw) data = JSON.parse(raw);
            } catch(e) {}
        }
        if (!data && this._collectors && slot === this._activeSlot) {
            console.warn(`[SaveSystem] Storage empty for slot ${slot}, exporting live state`);
            data = this.buildSaveData(this._collectors);
            data.slotIndex = slot;
        }
        if (!data) {
            console.warn(`[SaveSystem] Slot ${slot} is empty, nothing to export`);
            return;
        }

        const json = JSON.stringify(data);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `PnO_Save_Slot${slot}_${Date.now()}.json`;
        a.click();
        // Delay revoke: Safari/Firefox can revoke the blob before the download
        // dialog has finished reading it, producing an empty export file.
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    /**
     * Import save data from a JSON file.
     * @returns {Promise<object|null>} The imported SaveData
     */
    importSave(file, slot = this._activeSlot) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    let data = JSON.parse(e.target.result);

                    // ENG-08: top-level + nested type validation. Without these, a
                    // tampered save with `baseStats: "string"` crashes Map ops in
                    // variableManager.importState() and the player gets a white
                    // screen instead of an error toast.
                    if (!data || typeof data !== 'object' || Array.isArray(data)) {
                        reject(new Error('Invalid save: not a JSON object'));
                        return;
                    }
                    if (data.baseStats !== undefined && (typeof data.baseStats !== 'object' || data.baseStats === null || Array.isArray(data.baseStats))) {
                        reject(new Error('Invalid save: baseStats must be an object'));
                        return;
                    }
                    if (data.flags !== undefined && (typeof data.flags !== 'object' || data.flags === null)) {
                        reject(new Error('Invalid save: flags must be an object or array'));
                        return;
                    }
                    if (data.buffedStats !== undefined && (typeof data.buffedStats !== 'object' || data.buffedStats === null || Array.isArray(data.buffedStats))) {
                        reject(new Error('Invalid save: buffedStats must be an object'));
                        return;
                    }

                    // Validate required fields
                    if (data.ownedItemTags && !Array.isArray(data.ownedItemTags)) {
                        reject(new Error('Invalid save: ownedItemTags is not an array'));
                        return;
                    }
                    if (data.equippedSlots && data.equippedItemTags &&
                        data.equippedSlots.length !== data.equippedItemTags.length) {
                        reject(new Error('Invalid save: equipped slot/tag arrays length mismatch'));
                        return;
                    }

                    // Auto-convert Unity format if detected
                    if (isUnityFormat(data)) {
                        console.log('[SaveSystem] Converting imported Unity save to web format');
                        data = convertUnitySave(data);
                    }

                    // Basic validation
                    if (!data.inkState && !data.baseStats) {
                        reject(new Error('Invalid save file: missing required fields'));
                        return;
                    }
                    if (data.inkState && typeof data.inkState !== 'string') {
                        reject(new Error('Invalid save file: inkState is not a string'));
                        return;
                    }

                    // Run version migrations if needed
                    if ((data.saveVersion || 0) < CURRENT_SAVE_VERSION) {
                        this._migrate(data, slot);
                    }

                    data.slotIndex = slot;
                    const key = this._slotKey(slot);
                    await localforage.setItem(key, data);
                    this._metaCache.set(key, this._extractMeta(key, data));
                    console.log(`[SaveSystem] Imported save to slot ${slot}`);
                    resolve(data);
                } catch (err) {
                    reject(err);
                }
            };
            reader.onerror = () => reject(reader.error);
            reader.readAsText(file);
        });
    }

    // ---- Migration ----

    _migrate(data, slot) {
        const fromVersion = data.saveVersion || 0;
        console.log(`[SaveSystem] Migrating slot ${slot}: v${fromVersion} -> v${CURRENT_SAVE_VERSION}`);

        if (data.saveVersion < 1) {
            data.saveVersion = 1;
            data.gameVersion = data.gameVersion || '0.1.0';
        }

        if (data.saveVersion < 2) {
            data.smsMessages = data.smsMessages || [];
            data.saveVersion = 2;
        }

        if (data.saveVersion < 3) {
            // Migrate chastity/plug from Accessory to Front/Back
            if (data.equippedSlots && data.equippedItemTags) {
                for (let i = 0; i < data.equippedSlots.length; i++) {
                    if (data.equippedSlots[i] === 'Accessory') {
                        if (data.equippedItemTags[i] === 'chastity_cage') {
                            data.equippedSlots[i] = 'Front';
                        } else if (data.equippedItemTags[i] === 'plug') {
                            data.equippedSlots[i] = 'Back';
                        }
                    }
                }
            }
            data.saveVersion = 3;
        }

        if (data.saveVersion < 4) {
            // Migrate thigh_highs -> Legwear, belly_piercing -> Piercing
            if (data.equippedSlots && data.equippedItemTags) {
                for (let i = 0; i < data.equippedSlots.length; i++) {
                    if (data.equippedSlots[i] === 'Feet' && data.equippedItemTags[i] === 'thigh_highs') {
                        data.equippedSlots[i] = 'Legwear';
                    } else if (data.equippedSlots[i] === 'Head' && data.equippedItemTags[i] === 'belly_piercing') {
                        data.equippedSlots[i] = 'Piercing';
                    }
                }
            }
            data.saveVersion = 4;
        }

        if (data.saveVersion < 5) {
            // Two-City: auto-discover downtown for existing saves (Day >= 5 + worked at bar)
            // The Ink variable will be set on next refreshView via _forceSyncAllFromInk
            data._pendingFlags = data._pendingFlags || {};
            if (data.inkState) {
                // Flag for variableManager to set after story init
                data._pendingFlags.downtown_discovered = true;
            }
            data.saveVersion = 5;
        }

        if (data.saveVersion < 6) {
            // Migrate ankle_bracelet from Accessory to Accessory2 slot
            if (data.equippedSlots && data.equippedItemTags) {
                for (let i = 0; i < data.equippedSlots.length; i++) {
                    if (data.equippedSlots[i] === 'Accessory' && data.equippedItemTags[i] === 'ankle_bracelet') {
                        data.equippedSlots[i] = 'Accessory2';
                    }
                }
            }
            data.saveVersion = 6;
        }

        if (data.saveVersion < 7) {
            // Migrate ear_studs/hoop_earrings from Head to Piercing multi-slot
            // Also migrate ankle_bracelet from Accessory2 to generic Accessory routing
            if (data.equippedSlots && data.equippedItemTags) {
                const piercingTags = new Set(['ear_studs', 'hoop_earrings']);
                let nextPiercing = 0; // 0=Piercing, 1=Piercing2
                // First pass: find existing Piercing slot occupant
                for (let i = 0; i < data.equippedSlots.length; i++) {
                    if (data.equippedSlots[i] === 'Piercing') nextPiercing = 1;
                }
                for (let i = 0; i < data.equippedSlots.length; i++) {
                    if (data.equippedSlots[i] === 'Head' && piercingTags.has(data.equippedItemTags[i])) {
                        const target = nextPiercing === 0 ? 'Piercing' : nextPiercing === 1 ? 'Piercing2' : 'Piercing3';
                        data.equippedSlots[i] = target;
                        nextPiercing++;
                    }
                }
            }
            data.saveVersion = 7;
        }

        if (data.saveVersion < 8) {
            // Sarah Rework v0.2.4: girlfriend_suspicion/girlfriend_noticed_changes -> sarah_tension migration
            // happens inside Ink (sarah_migrate_save knot, fired from overnight_processing).
            // Nothing to do at the JS layer, old VARs remain in the Ink state for backcompat,
            // and the Ink knot reads them on first overnight tick to seed the new sarah_* vars.
            data.saveVersion = 8;
        }

        if (data.saveVersion < 9) {
            // G5/G3/G6 bump (audit 2026-05-24). JS-side migrations for this
            // version are handled inside Ink overnight_processing (Sleep.ink):
            // - apartment_expansion_enabled + comfort_training_enabled were
            //   force-flipped to true here (G3 kill-switch heal); both flags
            //   stripped after graduation (see tools/killswitch_registry.json)
            // - fem_last_gain_day seeded to current Day if still default 0
            //   (G6 prevents stagnation SMS firing on first overnight)
            // Nothing to do here, bump the version so future migrations
            // have a stable baseline. Recovery-load snapshot extension
            // (G1) is JS-side but doesn't need a save-version gate.
            data.saveVersion = 9;
        }

        if (data.saveVersion < 10) {
            // v0.2.8: the standalone Anal Trainer Kit item was removed and folded
            // into the large-plug comfort-training path. Strip the dead tag from
            // old saves so it doesn't trigger an "unknown item tag" warning on load
            // and isn't re-saved. anal_trained itself lives in inkState and is
            // preserved untouched, players who already earned the -1 Energy anal
            // discount keep it; mid-training players now earn it via the large plug.
            const DEPRECATED_ITEM_TAGS = new Set(['anal_trainer']);
            if (Array.isArray(data.ownedItemTags)) {
                data.ownedItemTags = data.ownedItemTags.filter(t => !DEPRECATED_ITEM_TAGS.has(t));
            }
            if (Array.isArray(data.equippedSlots) && Array.isArray(data.equippedItemTags)) {
                const keepSlots = [];
                const keepTags = [];
                for (let i = 0; i < data.equippedItemTags.length; i++) {
                    if (DEPRECATED_ITEM_TAGS.has(data.equippedItemTags[i])) continue;
                    keepSlots.push(data.equippedSlots[i]);
                    keepTags.push(data.equippedItemTags[i]);
                }
                data.equippedSlots = keepSlots;
                data.equippedItemTags = keepTags;
            }
            data.saveVersion = 10;
        }

        return data;
    }

    // ---- Helpers ----

    _slotKey(slot) {
        return `${SLOT_PREFIX}${slot}`;
    }

    _extractMeta(key, data) {
        if (!data || typeof data !== 'object') return { slot: -1, timestamp: null, day: 0, playerName: 'Unknown', gameVersion: '?', path: null, fem: 0, sub: 0 };
        const slot = key === AUTO_SAVE_KEY ? -1 : parseInt(key.replace(SLOT_PREFIX, ''));
        // Audit D15: extended meta for save-slot UI, path-indicator pill +
        // stat preview (Fem/Sub) so slots are distinguishable at a glance.
        const stats = data.baseStats || {};
        // master_intensity is non-observed, so baseStats may carry a stale value.
        // Prefer the route-VAR snapshot (data.inkVars), which captures the live
        // Ink value at save time; fall back to baseStats only if absent.
        const mi = data.inkVars?.master_intensity ?? stats.master_intensity ?? 0;
        const pathMap = ['Independent', 'Roommate', 'Landlord', 'Owner'];
        return {
            slot,
            timestamp: data.timestamp,
            day: data.day ?? stats.Day ?? 0,
            playerName: data.playerName ?? stats.PlayerName ?? 'Jamie',
            gameVersion: data.gameVersion ?? '?',
            path: pathMap[mi] || 'Independent',
            fem: stats.Femininity ?? 0,
            sub: stats.Submissiveness ?? 0,
        };
    }
}

export const saveSystem = new SaveSystem();
